/* eslint-disable max-lines */
// One service class fronting every ls_rag endpoint plus its response
// interfaces — splitting it by endpoint group would scatter types that
// callers already import from this single module. Same call as
// ObsMetricSubTabs.tsx's.
import { AppSettings } from '../../../AppSettings';
import { RoutingMode, IEstimateQueryResponse } from '../../models/GenAIModels';

const BASE = AppSettings.DCABackendServer;

function getHeaders(): HeadersInit {
  const token    = window.localStorage.getItem('accessToken') ?? '';
  const userType = window.localStorage.getItem('userType') ?? '';
  const userId   = window.localStorage.getItem('userId') ?? '';
  return {
    Authorization: `Bearer ${token}`,
    user_type: userType,
    userid: userId,
  };
}

async function fetchJSON<T>(url: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, { cache: 'no-store', ...init });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`HTTP ${res.status}: ${text}`);
  }
  return res.json() as Promise<T>;
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ILsRagDocument {
  id: string;
  name: string;
  drug_name: string;
  source: string;
  chunk_count: number;
  created_at: string;
  created_by: string;
}

export interface ILsRagSession {
  id: string;
  user_id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface ILsRagSource {
  document_name: string;
  drug_name: string;
  chunk_index: number;
  similarity: number;
  excerpt: string;
}

export interface ILsRagMessage {
  id: string;
  session_id: string;
  role: 'user' | 'assistant';
  content: string;
  sources?: ILsRagSource[];
  groundedness?: string;
  created_at: string;
  model?: string;
  tier?: string;
  cacheHit?: boolean;
  cost?: number;
}

export interface ILsRagQueryResponse {
  session_id: string;
  answer: string;
  key_facts: string[];
  groundedness: number;
  groundedness_rationale: string;
  sources: ILsRagSource[];
  latency_ms: number;
  model?: string;
  tier?: string;
  cache_hit?: boolean;
  cost?: number;
}

export interface ILsRagMetrics {
  total_queries: number;
  avg_groundedness: number | null;
  avg_latency_ms: number | null;
  active_sessions_24h: number;
  documents_indexed: number;
  chunks_indexed: number;
  // Same underlying signal (status = 'success'/'failed' on each logged
  // invocation), read two ways — all-time track record vs. current
  // operational health over the last 24h. Null until at least one
  // invocation has been logged.
  invocation_success_rate: number | null;
  agent_availability_pct: number | null;
  // Up/down badge — prefers agent_availability_pct (24h), falls back to
  // invocation_success_rate (all-time) when there's no 24h traffic.
  // 'unknown' only when neither has any data yet.
  agent_status: 'up' | 'degraded' | 'down' | 'unknown';
  avg_step_count: number | null;
  step_failure_rate_pct: number | null;
  // Per logical step (Dependency Mapper, Cost Estimator, Risk Assessor, Wave
  // Planner, synthesis) — empty for single-call agents, which never write
  // agent_invocation_steps rows.
  step_breakdown: { step_name: string; run_count: number; avg_latency_ms: number | null; failure_rate_pct: number | null }[];
  groundedness_distribution: Record<string, number>;
  avg_faithfulness: number | null;
  avg_answer_relevancy: number | null;
  avg_context_precision: number | null;
  // DeepEval GEval "Clinical Caution" — judged by Gemma, not the fixed
  // Gemini judge model the fields above use. See clinical_caution_service.py.
  avg_clinical_caution: number | null;
  abstention_rate: number | null;
  hallucination_rate: number | null;
  p95_latency_ms: number | null;
  p99_latency_ms: number | null;
  avg_retrieval_latency_ms: number | null;
  avg_generation_latency_ms: number | null;
  avg_top_similarity: number | null;
  avg_similarity_variance: number | null;
  coverage_gap_rate: number | null;
  rephrase_rate: number | null;
  avg_doc_age_days: number | null;
  oldest_doc_age_days: number | null;
  avg_session_depth: number | null;
  follow_up_rate: number | null;
  total_sessions: number;
  // Quality judges
  avg_coherence: number | null;
  avg_completeness: number | null;
  avg_factual_consistency: number | null;
  avg_citation_accuracy: number | null;
  avg_toxicity_score: number | null;
  pii_detection_rate: number | null;
  injection_rate: number | null;
  off_label_rate: number | null;
  // Cost
  total_estimated_cost_usd: number | null;
  avg_cost_per_query_usd: number | null;
  total_prompt_tokens: number | null;
  total_completion_tokens: number | null;
  // UX
  user_satisfaction_rate: number | null;
  // Config thresholds exposed from backend
  coverage_sim_threshold: number;
  hallucination_threshold: number;
  recent_queries: Array<{
    session_id: string;
    query: string;
    retrieved_count: number;
    groundedness_score: string;
    latency_ms: number;
    retrieval_latency_ms?: number | null;
    generation_latency_ms?: number | null;
    created_at: string;
    faithfulness?: string | null;
    answer_relevancy?: string | null;
    context_precision?: string | null;
    is_abstention?: boolean | null;
    prompt_tokens?: number | null;
    completion_tokens?: number | null;
    estimated_cost_usd?: number | null;
    coherence_score?: number | null;
    completeness_score?: number | null;
    factual_consistency?: number | null;
    citation_accuracy?: number | null;
    pii_detected?: boolean | null;
    prompt_injection?: boolean | null;
    off_label_flagged?: boolean | null;
    is_rephrase?: boolean | null;
    clinical_caution_score?: number | null;
    clinical_caution_rationale?: string | null;
  }>;
}

// Tool & MCP Integration Health — only populated for agents with
// ObservabilityCapabilities.tool_mcp_health=True (DevOps Copilot today).
// Backed by agent_tool_calls, written on every github_tools.dispatch() call
// in agents_service.py's _run_github_tool_loop.
export interface IToolHealthMetrics {
  tools: Array<{
    tool_name: string;
    total_calls: number;
    success_rate: number | null;
    avg_latency_ms: number | null;
    p95_latency_ms: number | null;
  }>;
  recent_failures: Array<{ tool_name: string; error_message: string | null; called_at: string }>;
  daily_call_volume: Array<{ day: string; n: number }>;
  mcp_servers: Array<{ name: string; transport: string; tools: string[]; status: 'up' | 'degraded' | 'down' }>;
}

export interface ICostEfficiencyMetrics {
  query_count: number;
  total_tokens: number | null; total_input_tokens: number | null; total_output_tokens: number | null;
  tokens_saved_via_caching: number | null;
  total_cost_usd: number | null;
  cost_per_1k_tokens_by_model: Array<{ model: string; cost_per_1k_tokens: number | null }>;
  tokens_by_model: Array<{ model: string; tokens: number }>;
  cost_per_query_usd: number | null;
  cache_hit_rate_pct: number | null;
  cost_avoided_via_caching_usd: number | null;
  cost_saved_via_routing_usd: number | null; total_cost_savings_usd: number | null;
  cost_by_model_class: { open_weight_usd: number; closed_weight_usd: number };
  discrimination_adjusted_cost_by_model: Array<{ model: string; cost_per_quality_point: number | null }>;
  budget_cap_usd: number;
  month_to_date_cost_usd: number | null; budget_utilization_pct: number | null;
  cost_last_month_usd: number | null; cost_mom_change_pct: number | null;
  spend_trend: Array<{ date: string; cost_usd: number | null }>;
  spend_by_model_trend: Array<Record<string, string | number>>; spend_by_model_names: string[];
  projected_monthly_run_rate_usd: number | null;
}

// App & infra availability — agent-agnostic, not scoped to any one agent the
// way the other observability metrics are. Three genuinely distinct infra
// surfaces, not one: the agents themselves run on Cloud Run (dca_app), the
// LiteLLM proxy runs on EKS (litellm_proxy), and Gemma/OLMo3 are plain EC2
// instances (model_hosts) — conflating these was an earlier mistake this
// shape no longer makes. Each section degrades independently server-side:
// `available: false` means that metric isn't wired yet (e.g. Grafana not
// configured, or no blackbox probes set up), not an error.
export interface IInfraHealthMetrics {
  dca_app: {
    available: boolean;
    cloud_run: {
      available: boolean;
      note?: string;
      services: Array<{
        service: string;
        available: boolean;
        requests_per_sec?: number | null;
        error_rate_pct?: number | null;
        p99_latency_ms?: number | null;
        instance_count?: number | null;
        cpu_utilization_pct?: number | null;
        memory_utilization_pct?: number | null;
        max_concurrent_requests?: number | null;
      }>;
    };
    postgres: {
      available: boolean;
      total_connections?: number;
      connections_by_state?: Record<string, number>;
      longest_active_query_sec?: number;
      deadlocks?: number | null;
      cache_hit_ratio_pct?: number | null;
      database_size_bytes?: number | null;
    };
  };
  litellm_proxy: {
    namespace: string;
    pods: { available: boolean; total: number; running: number; not_running: number; restarts_1h: number; restarts_by_pod?: Record<string, number> };
    cpu: { available: boolean; series: Array<{ pod: string; cores: number }> };
    memory: { available: boolean; series: Array<{ pod: string; bytes: number }> };
  };
  model_hosts: {
    available: boolean;
    instances: Array<{
      instance_id: string;
      name: string;
      component: string;
      state: string;
      cpu_utilization_pct: number | null;
      status_check_failed: boolean | null;
      network_in_bytes_per_sec: number | null;
      network_out_bytes_per_sec: number | null;
    }>;
    note?: string;
  };
  uptime: { available: boolean; probes: Array<{ instance: string; up: boolean; error?: string | null }>; note?: string };
  // Rolled up server-side from all 4 tiers above — see
  // infra_health_service.py's _rollup_app_status for the priority rules
  // (down > degraded > up > unknown; a tier that isn't configured is
  // neutral, not a failure).
  overall_status: { status: 'up' | 'degraded' | 'down' | 'unknown'; reasons: string[]; tiers_reporting: string };
}

export interface ITraceObservation {
  id: string;
  name: string;
  type: string;
  latency_ms: number | null;
  output: string | null;
}

export interface ITrace {
  id: string;
  name: string;
  query: string | null;
  latency_ms: number | null;
  total_cost: number | null;
  created_at: string | null;
  session_id: string | null;
  langfuse_trace_id: string | null;
  observations: ITraceObservation[];
}

export interface IEvalRun {
  run_id?: string;
  id?: string;
  run_name: string;
  model: string;
  dataset: string;
  total_queries: number;
  avg_faithfulness: number | null;
  avg_answer_relevancy: number | null;
  avg_context_precision: number | null;
  avg_groundedness: number | null;
  discrimination_score: number | null;
  abstention_rate: number | null;
  avg_latency_ms: number | null;
  by_type: Record<string, { count: number; avg_faithfulness: number | null; avg_answer_relevancy: number | null; avg_context_precision: number | null; avg_groundedness: number | null }>;
  created_at?: string;
  created_by?: string;
}

export interface IEvalComparisonMetric {
  run_a: number | null;
  run_b: number | null;
  delta: number | null;
  improved: boolean;
}

export interface IEvalComparison {
  run_a: { id: string; run_name: string; model: string };
  run_b: { id: string; run_name: string; model: string };
  comparison: Record<string, IEvalComparisonMetric>;
}

export interface IAlertAnalysis {
  rca_bullets: string[];
  steps: string[];
}

export interface IGoldenSetResult {
  total: number;
  discrimination_score: number | null;
  by_type: Record<string, {
    count: number;
    avg_faithfulness: number | null;
    avg_answer_relevancy: number | null;
    avg_context_precision: number | null;
    avg_groundedness: number | null;
  }>;
  entries: Array<{
    id: string;
    type: string;
    query: string;
    answer?: string;
    groundedness?: number;
    faithfulness?: number | null;
    answer_relevancy?: number | null;
    context_precision?: number | null;
    error?: string;
  }>;
}

// ── Service ───────────────────────────────────────────────────────────────────

class LifeSciencesRagService {
  // Documents
  listDocuments(): Promise<{ documents: ILsRagDocument[]; count: number }> {
    return fetchJSON(`${BASE}/ls-rag/documents`, { headers: getHeaders() });
  }

  uploadDocument(file: File, drugName: string): Promise<{ document_id: string; chunk_count: number }> {
    const form = new FormData();
    form.append('file', file);
    form.append('drug_name', drugName);
    return fetchJSON(`${BASE}/ls-rag/documents/upload`, {
      method: 'POST',
      headers: getHeaders(),
      body: form,
    });
  }

  deleteDocument(docId: string): Promise<{ deleted: boolean }> {
    return fetchJSON(`${BASE}/ls-rag/documents/${docId}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
  }

  // Sessions
  createSession(name?: string): Promise<ILsRagSession> {
    return fetchJSON(`${BASE}/ls-rag/sessions`, {
      method: 'POST',
      headers: { ...getHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    });
  }

  listSessions(): Promise<{ sessions: ILsRagSession[]; count: number }> {
    return fetchJSON(`${BASE}/ls-rag/sessions`, { headers: getHeaders() });
  }

  deleteSession(sessionId: string): Promise<{ deleted: boolean }> {
    return fetchJSON(`${BASE}/ls-rag/sessions/${sessionId}`, {
      method: 'DELETE',
      headers: getHeaders(),
    });
  }

  async getMessages(sessionId: string): Promise<{ messages: ILsRagMessage[]; count: number }> {
    const res = await fetchJSON<{ messages: Array<ILsRagMessage & { cache_hit?: boolean }>; count: number }>(
      `${BASE}/ls-rag/sessions/${sessionId}/messages`,
      { headers: getHeaders() },
    );
    // The backend returns raw DB column names (cache_hit); map to the
    // camelCase field the rest of the app uses, matching the convention
    // already used for the live query response (see handleSend's cacheHit:
    // res.cache_hit).
    return {
      ...res,
      messages: res.messages.map(({ cache_hit, ...m }) => ({ ...m, cacheHit: cache_hit })),
    };
  }

  // Query
  query(
    question: string,
    sessionId?: string,
    relaxed?: boolean,
    model?: string,
    routingMode?: RoutingMode,
    disableCache?: boolean,
  ): Promise<ILsRagQueryResponse> {
    return fetchJSON(`${BASE}/ls-rag/query`, {
      method: 'POST',
      headers: { ...getHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        question,
        session_id: sessionId,
        relaxed: relaxed ?? false,
        model,
        routing_mode: routingMode,
        disable_cache: disableCache,
      }),
    });
  }

  estimateQuery(
    question: string,
    disableCache?: boolean,
    model?: string,
    routingMode?: RoutingMode,
  ): Promise<IEstimateQueryResponse> {
    return fetchJSON(`${BASE}/ls-rag/estimate`, {
      method: 'POST',
      headers: { ...getHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        question,
        disable_cache: disableCache,
        model,
        routing_mode: routingMode,
      }),
    });
  }

  // Observability
  getMetrics(): Promise<ILsRagMetrics> {
    return fetchJSON(`${BASE}/ls-rag/observability`, { headers: getHeaders() });
  }

  getAgentMetrics(agentId: string): Promise<ILsRagMetrics> {
    return fetchJSON(`${BASE}/agents/observability?agent_id=${encodeURIComponent(agentId)}`, { headers: getHeaders() });
  }

  getAgentCostMetrics(agentId: string): Promise<ICostEfficiencyMetrics> {
    return fetchJSON(`${BASE}/agents/observability/cost-efficiency?agent_id=${encodeURIComponent(agentId)}`, { headers: getHeaders() });
  }

  getAgentToolHealth(agentId: string): Promise<IToolHealthMetrics> {
    return fetchJSON(`${BASE}/agents/observability/tool-health?agent_id=${encodeURIComponent(agentId)}`, { headers: getHeaders() });
  }

  getHealth(): Promise<{ status: string; documents_indexed: number; total_queries: number }> {
    return fetchJSON(`${BASE}/ls-rag/observability/health`, { headers: getHeaders() });
  }

  getCostEfficiencyMetrics(feature = 'ls-rag'): Promise<ICostEfficiencyMetrics> {
    return fetchJSON(`${BASE}/ls-rag/observability/cost-efficiency?feature=${encodeURIComponent(feature)}`, { headers: getHeaders() });
  }

  // Agent-agnostic — no agent_id/feature param, unlike every other
  // observability call above. Pod/node/CPU/memory health belongs to the
  // shared EKS infra, not any one agent.
  getInfraHealth(): Promise<IInfraHealthMetrics> {
    return fetchJSON(`${BASE}/observability/infra-health`, { headers: getHeaders() });
  }

  evaluateGoldenSet(): Promise<IGoldenSetResult> {
    return fetchJSON(`${BASE}/ls-rag/evaluate-golden-set`, { headers: getHeaders() });
  }

  submitFeedback(sessionId: string, messageId: string, rating: 1 | -1): Promise<{ recorded: boolean }> {
    return fetchJSON(`${BASE}/ls-rag/feedback`, {
      method: 'POST',
      headers: { ...getHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ session_id: sessionId, message_id: messageId, rating }),
    });
  }

  getTraces(limit?: number): Promise<{ traces: ITrace[]; langfuse_enabled: boolean; langfuse_url: string | null }> {
    const qs = limit ? `?limit=${limit}` : '';
    return fetchJSON(`${BASE}/ls-rag/traces${qs}`, { headers: getHeaders() });
  }

  getTraceDetail(langfuseTraceId: string): Promise<{ observations: ITraceObservation[] }> {
    return fetchJSON(`${BASE}/ls-rag/traces/${encodeURIComponent(langfuseTraceId)}`, { headers: getHeaders() });
  }

  getEvalRuns(limit?: number): Promise<{ runs: IEvalRun[] }> {
    const qs = limit ? `?limit=${limit}` : '';
    return fetchJSON(`${BASE}/ls-rag/eval/runs${qs}`, { headers: getHeaders() });
  }

  getEvalRun(runId: string): Promise<IEvalRun> {
    return fetchJSON(`${BASE}/ls-rag/eval/runs/${runId}`, { headers: getHeaders() });
  }

  compareEvalRuns(runA: string, runB: string): Promise<IEvalComparison> {
    return fetchJSON(`${BASE}/ls-rag/eval/compare?run_a=${runA}&run_b=${runB}`, { headers: getHeaders() });
  }

  triggerEvalRun(model: string, runName: string, maxEntries?: number): Promise<IEvalRun> {
    return fetchJSON(`${BASE}/ls-rag/eval/run`, {
      method: 'POST',
      headers: { ...getHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ model, run_name: runName, ...(maxEntries != null && { max_entries: maxEntries }) }),
    });
  }

  analyzeAlert(payload: {
    context_type: 'metric' | 'hallucination';
    metric_name?: string;
    current_value?: number;
    threshold?: number;
    direction?: string;
    query?: string;
    groundedness_score?: number;
    faithfulness?: number | null;
    answer_relevancy?: number | null;
    context_precision?: number | null;
  }): Promise<IAlertAnalysis> {
    return fetchJSON(`${BASE}/ls-rag/alerts/analyze`, {
      method: 'POST',
      headers: { ...getHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  }
}

export default new LifeSciencesRagService();
